import { PITCH_LIMIT } from "@fragout/shared";

/** Keyboard and mouse into the shape the input schema wants. Presses are buffered and consumed once. */
export class InputState {
  private keys = new Set<string>();
  private jumpPressed = false;
  private buttons = new Set<number>();
  private latched = new Set<string>();   // presses seen since the last step, consumed once
  yaw = 0;
  pitch = 0;
  sensitivity = 0.0022;
  locked = false;
  scoreboard = false;
  slot = 1;
  private lastSlot = 1;
  enabled = true;

  constructor(private canvas: HTMLCanvasElement) {
    window.addEventListener("keydown", (e) => {
      if (e.repeat) return;
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA") return;
      this.keys.add(e.code);
      this.latched.add(e.code);
      if (e.code === "Space") { this.jumpPressed = true; e.preventDefault(); }
      if (e.code === "Tab") { this.scoreboard = true; e.preventDefault(); }
      if (e.code === "ControlLeft" || e.code === "ControlRight") e.preventDefault();
      if (e.code === "Digit1") this.selectSlot(1);
      if (e.code === "Digit2") this.selectSlot(2);
      if (e.code === "Digit3") this.selectSlot(3);
      if (e.code === "Digit4") this.selectSlot(4);
      if (e.code === "KeyQ") this.selectSlot(this.lastSlot);
    });
    window.addEventListener("keyup", (e) => {
      this.keys.delete(e.code);
      if (e.code === "Tab") { this.scoreboard = false; e.preventDefault(); }
    });
    window.addEventListener("blur", () => { this.keys.clear(); this.buttons.clear(); });
    document.addEventListener("pointerlockchange", () => {
      this.locked = document.pointerLockElement === this.canvas;
      if (this.locked) this.lockError = "";
      if (!this.locked) this.buttons.clear();
    });
    window.addEventListener("mousemove", (e) => {
      if (!this.locked) return;
      // Yaw grows clockwise: forward is (sin yaw, cos yaw) and the camera takes yaw as is, so a
      // rightward push has to add. Pitch subtracts, which points the view up as the mouse goes up.
      this.yaw += e.movementX * this.sensitivity;
      this.pitch -= e.movementY * this.sensitivity;
      if (this.pitch > PITCH_LIMIT) this.pitch = PITCH_LIMIT;
      if (this.pitch < -PITCH_LIMIT) this.pitch = -PITCH_LIMIT;
      if (this.yaw > Math.PI) this.yaw -= Math.PI * 2;
      if (this.yaw < -Math.PI) this.yaw += Math.PI * 2;
    });
    // Pointer events, not mouse events. The renderer cancels pointerdown, and a cancelled pointerdown
    // suppresses the compatibility mousedown, so a mousedown listener here would never fire.
    canvas.addEventListener("pointerdown", (e) => {
      if (!this.locked) { if (this.enabled) void this.lock(); return; }
      this.buttons.add(e.button);
      this.latched.add("Mouse" + e.button);
      e.preventDefault();
    });
    window.addEventListener("pointerup", (e) => { this.buttons.delete(e.button); });
    window.addEventListener("mouseup", (e) => { this.buttons.delete(e.button); });
    canvas.addEventListener("contextmenu", (e) => e.preventDefault());
    canvas.addEventListener("wheel", (e) => {
      if (!this.locked) return;
      const dir = e.deltaY > 0 ? 1 : -1;
      let s = this.slot;
      for (let i = 0; i < 4; i++) { s = ((s - 1 + dir + 4) % 4) + 1; if (s !== this.slot) break; }
      this.selectSlot(s);
      e.preventDefault();
    }, { passive: false });
  }

  /** Ask for the mouse. Chrome only grants this from a user gesture, and refuses for a second after an Esc. */
  async lock(): Promise<void> {
    if (this.locked || !this.enabled) return;
    try {
      const r = this.canvas.requestPointerLock() as unknown as Promise<void> | undefined;
      if (r && typeof r.then === "function") await r;
      this.lockError = "";
    } catch (err) {
      this.lockError = (err as Error).message ?? String(err);
    }
  }
  lockError = "";

  selectSlot(s: number) {
    if (s === this.slot) return;
    this.lastSlot = this.slot;
    this.slot = s;
  }

  get moveX(): number { return this.enabled ? (this.keys.has("KeyD") ? 1 : 0) - (this.keys.has("KeyA") ? 1 : 0) : 0; }
  get moveZ(): number { return this.enabled ? (this.keys.has("KeyW") ? 1 : 0) - (this.keys.has("KeyS") ? 1 : 0) : 0; }
  get crouch(): boolean { return this.enabled && (this.keys.has("ControlLeft") || this.keys.has("ControlRight") || this.keys.has("KeyC")); }
  get fire(): boolean { return this.enabled && this.locked && this.buttons.has(0); }
  get kick(): boolean { return this.enabled && this.keys.has("KeyF"); }
  get stab(): boolean { return this.enabled && (this.keys.has("KeyG") || (this.locked && this.buttons.has(2))); }
  get interact(): boolean { return this.enabled && this.keys.has("KeyE"); }

  /** Held now, or tapped since the last step. Call once per step so a tap shorter than a frame still lands. */
  take(code: string, held: boolean): boolean {
    const tapped = this.latched.has(code);
    this.latched.delete(code);
    return this.enabled && (held || tapped);
  }
  /** Clear the tap latches once every step has been staged this frame, so a press made while dead or between matches does not fire later. */
  endStep(): void { this.latched.clear(); this.jumpPressed = false; }

  /** True once per press, on the step that consumes it. */
  takeJump(): boolean {
    const j = this.jumpPressed && this.enabled;
    this.jumpPressed = false;
    return j;
  }
}
